/**
 * Page help in the header (docs/STYLES.md "Inline help"): a page's
 * `HelpDocumentation` no longer sits at the top of the content — it registers
 * here, and the page's `PageHeader` shows one quiet "How it works" link that
 * opens every registered help section in a side sheet.
 *
 * - `PageHelpProvider` wraps the page area (`AppLayout`), so a project tab's
 *   help reaches the project page's header.
 * - A `PageHeader` announces itself (`usePageHelpHeader`). With no header on
 *   screen, or outside a provider, `HelpDocumentation` falls back to its inline
 *   collapsible panel, so help is never lost.
 * - Entries are kept in refs, not state, so a help section re-rendering with
 *   the page never re-renders the provider; only mount / unmount do. The sheet
 *   reads the latest content when it renders.
 */
import { useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useOrgBasePath } from '@/hooks/useOrgNavigate';
import { useTranslation } from '@/hooks/useTranslation';
import { PageHelpContext, type PageHelpContextValue, type PageHelpEntry } from './page-help-context';

export function PageHelpProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [ids, setIds] = useState<string[]>([]);
  const [headerCount, setHeaderCount] = useState(0);
  const entries = useRef(new Map<string, PageHelpEntry>());

  const register = useCallback((id: string) => {
    setIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    return () => {
      entries.current.delete(id);
      setIds((prev) => prev.filter((x) => x !== id));
    };
  }, []);
  const registerHeader = useCallback(() => {
    setHeaderCount((n) => n + 1);
    return () => setHeaderCount((n) => n - 1);
  }, []);

  const value = useMemo(
    () => ({ ids, entries, register, headerCount, registerHeader }),
    [ids, register, headerCount, registerHeader],
  );
  return <PageHelpContext.Provider value={value}>{children}</PageHelpContext.Provider>;
}

/**
 * The header's "How it works" link and its side sheet; nothing when the page has no help.
 * `PageHeader` renders on pages outside an organization too, so the org and translation hooks
 * live in the inner component, mounted only when there is help to show.
 */
export function PageHelpLink() {
  const ctx = useContext(PageHelpContext);
  if (!ctx || ctx.ids.length === 0) return null;
  return <PageHelpLinkInner ctx={ctx} />;
}

function PageHelpLinkInner({ ctx }: Readonly<{ ctx: PageHelpContextValue }>) {
  const { t } = useTranslation('common');
  const orgBasePath = useOrgBasePath();
  const [open, setOpen] = useState(false);
  const sections = ctx.ids
    .map((id) => [id, ctx.entries.current.get(id)] as const)
    .filter((pair): pair is readonly [string, PageHelpEntry] => !!pair[1]);
  if (sections.length === 0) return null;
  const [, first] = sections[0];

  return (
    <>
      {/* Text only, baseline-aligned with the title by PageHeader. */}
      <Button type="button" variant="link" className="h-auto p-0 text-sm font-medium" onClick={() => setOpen(true)}>
        {t('help.howItWorks')}
      </Button>
      {open && (
        <Sheet open onOpenChange={setOpen}>
          <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl">
            <SheetHeader>
              <SheetTitle>{first.title}</SheetTitle>
              {/* Descriptions were written to follow "Click to learn about…": capitalise the first letter. */}
              {first.description && <SheetDescription className="first-letter:uppercase">{first.description}</SheetDescription>}
            </SheetHeader>
            <div className="mt-6 space-y-8">
              {sections.map(([id, entry], i) => (
                <section key={id} className="space-y-4 text-sm text-releve-teal-dark">
                  {/* The first section's title is the sheet's; later ones carry their own. */}
                  {i > 0 && <h3 className="text-base font-medium text-foreground">{entry.title}</h3>}
                  {entry.content}
                  {entry.learnMoreSlug && (
                    <Link
                      to={`${orgBasePath}/help/${entry.learnMoreSlug}`}
                      className="block text-xs text-releve-teal underline hover:text-releve-teal-dark"
                    >
                      {t('help.readMore')} &rarr;
                    </Link>
                  )}
                </section>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
